import { useEffect, useState } from 'react';

import { extractErrorMessage } from '../../api/errors';

/**
 * Validate a numeric-setting draft against its persisted value and range.
 * Pure so the branchy valid/dirty logic (shared by the Workers page's two
 * near-identical forms) is unit-testable without rendering.
 *   - valid: a whole number within [min, max]
 *   - dirty: differs from the currently-persisted value (null = not loaded)
 */
export function numericSettingState(
  draft: string,
  current: number | null,
  min: number,
  max: number,
): { num: number; valid: boolean; dirty: boolean } {
  const num = Number.parseInt(draft, 10);
  const valid = Number.isFinite(num) && num >= min && num <= max;
  const dirty = current !== null && num !== current;
  return { num, valid, dirty };
}

export interface NumericSetting {
  draft: string;
  setDraft: (v: string) => void;
  valid: boolean;
  dirty: boolean;
  saving: boolean;
  canSave: boolean;
  error: string | null;
  savedAt: number | null;
  save: () => Promise<void>;
}

/**
 * State + persistence for one editable numeric setting (a text field + Save
 * that PATCHes and surfaces success/error). Factored out so the Workers
 * page's replica-count and api-process forms — previously two copies of the
 * same draft/valid/dirty/saving/error/savedAt machinery — share one
 * implementation.
 *
 * The draft seeds (and re-seeds after a successful save) from `current`, so
 * once the persisted value lands the field shows it and Save stays disabled
 * until the operator actually changes it.
 */
export function useNumericSetting(opts: {
  current: number | null;
  min: number;
  max: number;
  persist: (value: number) => Promise<void>;
  fallbackError: string;
}): NumericSetting {
  const { current, min, max, persist, fallbackError } = opts;
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (current !== null) setDraft(String(current));
  }, [current]);

  const { num, valid, dirty } = numericSettingState(draft, current, min, max);
  const canSave = valid && dirty && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setSavedAt(null);
    try {
      await persist(num);
      setSavedAt(Date.now());
    } catch (err) {
      setError(extractErrorMessage(err, fallbackError));
    } finally {
      setSaving(false);
    }
  };

  return {
    draft,
    setDraft,
    valid,
    dirty,
    saving,
    canSave,
    error,
    savedAt,
    save,
  };
}
